<div class="animate-fade-in w-full space-y-6">
  <!-- Step 1: Account Creation -->
  @if (currentView === ViewState.ACCOUNT) {
    <!-- Welcome Text -->
    <div class="text-center md:text-left">
      <h2 class="mb-2 text-2xl font-bold text-greyscale-900">
        {{ "register.createYourAccount" | transloco }}
      </h2>
    </div>

    <form [formGroup]="accountForm" class="flex w-full flex-col gap-4">
      <!-- Email Field -->
      <div class="w-full">
        <label
          for="email"
          class="mb-1 block text-sm font-medium text-greyscale-700"
          >{{ "register.email" | transloco }}</label
        >
        <input
          id="email"
          type="email"
          formControlName="email"
          [attr.placeholder]="'register.email' | transloco"
          class="w-full rounded-md border border-greyscale-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
          [class.border-error-500]="
            accountForm.get('email')?.invalid &&
            accountForm.get('email')?.touched
          "
          [class.border-validation-500]="isEmailValid()"
        />

        <!-- Email Validation Messages -->
        @if (isEmailChecking()) {
          <div class="mt-1">
            <small class="text-primary-600">
              <i class="pi pi-spinner pi-spin mr-1"></i>
              {{ "register.checkingEmail" | transloco }}
            </small>
          </div>
        }

        @if (
          accountForm.get("email")?.errors &&
          accountForm.get("email")?.touched &&
          !isEmailChecking()
        ) {
          <div class="mt-1">
            @if (accountForm.get("email")?.errors?.["required"]) {
              <small class="text-error-600">
                {{ "register.emailRequired" | transloco }}
              </small>
            }
            @if (accountForm.get("email")?.errors?.["invalidEmailFormat"]) {
              <small class="text-error-600">
                {{ "register.invalidEmailFormat" | transloco }}
              </small>
            }
            @if (accountForm.get("email")?.errors?.["nonProfessionalEmail"]) {
              <small class="text-error-600">
                {{
                  accountForm.get("email")?.errors?.["nonProfessionalEmail"]
                    ?.message
                }}
              </small>
            }
          </div>
        }
      </div>

      <!-- First Name Field -->
      <div>
        <label
          for="firstName"
          class="mb-1 block text-sm font-medium text-greyscale-700"
          >{{ "register.firstName" | transloco }}</label
        >
        <input
          id="firstName"
          type="text"
          formControlName="firstName"
          [attr.placeholder]="'register.firstNamePlaceholder' | transloco"
          class="w-full rounded-md border border-greyscale-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
          [class.border-error-500]="
            accountForm.get('firstName')?.invalid &&
            accountForm.get('firstName')?.touched
          "
        />

        @if (
          accountForm.get("firstName")?.errors &&
          accountForm.get("firstName")?.touched
        ) {
          <div class="mt-1">
            @if (accountForm.get("firstName")?.errors?.["required"]) {
              <small class="text-error-600">
                {{ "register.firstNameRequired" | transloco }}
              </small>
            }
            @if (accountForm.get("firstName")?.errors?.["minlength"]) {
              <small class="text-error-600">
                {{ "register.firstNameTooShort" | transloco }}
              </small>
            }
            @if (accountForm.get("firstName")?.errors?.["maxlength"]) {
              <small class="text-error-600">
                {{ "register.firstNameTooLong" | transloco }}
              </small>
            }
          </div>
        }
      </div>

      <!-- Last Name Field -->
      <div>
        <label
          for="lastName"
          class="mb-1 block text-sm font-medium text-greyscale-700"
          >{{ "register.lastName" | transloco }}</label
        >
        <input
          id="lastName"
          type="text"
          formControlName="lastName"
          [attr.placeholder]="'register.lastNamePlaceholder' | transloco"
          class="w-full rounded-md border border-greyscale-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
          [class.border-error-500]="
            accountForm.get('lastName')?.invalid &&
            accountForm.get('lastName')?.touched
          "
        />

        @if (
          accountForm.get("lastName")?.errors &&
          accountForm.get("lastName")?.touched
        ) {
          <div class="mt-1">
            @if (accountForm.get("lastName")?.errors?.["required"]) {
              <small class="text-error-600">
                {{ "register.lastNameRequired" | transloco }}
              </small>
            }
            @if (accountForm.get("lastName")?.errors?.["minlength"]) {
              <small class="text-error-600">
                {{ "register.lastNameTooShort" | transloco }}
              </small>
            }
            @if (accountForm.get("lastName")?.errors?.["maxlength"]) {
              <small class="text-error-600">
                {{ "register.lastNameTooLong" | transloco }}
              </small>
            }
          </div>
        }
      </div>

      <!-- Email Already Taken Message -->
      @if (isEmailAlreadyTaken()) {
        <div class="rounded border-l-4 border-error-400 bg-error-50 p-3">
          <div class="flex">
            <div class="flex-shrink-0">
              <i class="pi pi-exclamation-triangle text-error-400"></i>
            </div>
            <div class="ml-3">
              <p class="mb-2 text-sm text-error-700">
                {{ "register.emailAlreadyRegistered" | transloco }}
              </p>
              <a
                routerLink="/auth/login"
                class="inline-flex items-center rounded-md border border-transparent bg-error-100 px-3 py-1.5 text-xs font-medium text-error-600 no-underline hover:bg-error-200 focus:outline-none focus:ring-2 focus:ring-error-500 focus:ring-offset-2"
              >
                <i class="pi pi-sign-in mr-1 text-xs"></i>
                {{ "register.logInInstead" | transloco }}
              </a>
            </div>
          </div>
        </div>
      }

      <!-- Company Exists Message for Professional Emails -->
      @if (showCompanyExistsMessage()) {
        <div class="rounded border-l-4 border-primary-400 bg-primary-50 p-3">
          <div class="flex">
            <div class="flex-shrink-0">
              <i class="pi pi-info-circle text-primary-400"></i>
            </div>
            <div class="ml-3">
              <p class="text-sm text-primary-700">
                @if (getExistingCompanyName()) {
                  <span>
                    {{
                      "register.companyAlreadyRegistered"
                        | transloco: { company: getExistingCompanyName() }
                    }}
                  </span>
                } @else {
                  <span>
                    {{ "register.companyDomainExists" | transloco }}
                  </span>
                }
              </p>
              <p class="mt-1 text-xs text-primary-600">
                {{ "register.requestAccessNextStep" | transloco }}
              </p>
            </div>
          </div>
        </div>
      }

      <!-- Password Field -->
      <div>
        <label
          for="password"
          class="mb-1 block text-sm font-medium text-greyscale-700"
          >{{ "register.password" | transloco }}</label
        >

        <!-- Password strength indicator -->
        <app-password-strength-indicator
          [control]="accountForm.get('password')!"
          translationPrefix="register"
        >
        </app-password-strength-indicator>

        <p-password
          id="password"
          formControlName="password"
          [placeholder]="'register.enterPassword' | transloco"
          [toggleMask]="true"
          [feedback]="false"
          styleClass="w-full"
          inputStyleClass="w-full px-3 py-2 border border-greyscale-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        ></p-password>

        @if (
          accountForm.get("password")?.errors &&
          accountForm.get("password")?.touched
        ) {
          <div class="mt-1">
            @if (accountForm.get("password")?.errors?.["required"]) {
              <small class="text-error-600">
                {{ "register.passwordRequired" | transloco }}
              </small>
            }
          </div>
        }
      </div>

      <!-- Confirm Password Field -->
      <div>
        <label
          for="confirmPassword"
          class="mb-1 block text-sm font-medium text-greyscale-700"
          >{{ "register.confirmPasswordLabel" | transloco }}</label
        >
        <input
          id="confirmPassword"
          type="password"
          formControlName="confirmPassword"
          [placeholder]="'register.confirmPasswordPlaceholder' | transloco"
          class="w-full rounded-md border border-greyscale-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
          [class.border-error-500]="
            accountForm.get('confirmPassword')?.invalid &&
            accountForm.get('confirmPassword')?.touched
          "
        />

        @if (
          accountForm.get("confirmPassword")?.errors &&
          accountForm.get("confirmPassword")?.touched
        ) {
          <div class="mt-1">
            @if (accountForm.get("confirmPassword")?.errors?.["required"]) {
              <small class="text-error-600">
                {{ "register.confirmPasswordRequired" | transloco }}
              </small>
            }
            @if (
              accountForm.get("confirmPassword")?.errors?.["passwordMismatch"]
            ) {
              <small class="text-error-600">
                {{ "register.passwordMismatch" | transloco }}
              </small>
            }
          </div>
        }
      </div>

      <!-- Terms Checkbox -->
      <div class="flex items-start">
        <div class="flex h-5 items-center">
          <p-checkbox
            id="agreedToTerms"
            formControlName="agreedToTerms"
            binary="true"
          ></p-checkbox>
        </div>
        <div class="ml-3 text-sm">
          <label for="agreedToTerms" class="cursor-pointer text-greyscale-700">
            {{ "register.agreeToTerms" | transloco }}
            <a
              [href]="conditionsUrl"
              target="_blank"
              class="text-primary-600 underline hover:text-primary-700"
              (click)="$event.stopPropagation()"
            >
              {{ "register.conditionsTooltip" | transloco }}
            </a>
          </label>
        </div>
      </div>
      

      @if (
        accountForm.get("agreedToTerms")?.errors &&
        accountForm.get("agreedToTerms")?.touched
      ) {
        <div class="mt-1">
          <small class="text-error-600">
            {{ "register.mustAgreeToTerms" | transloco }}
          </small>
        </div>
      }

      <!-- Privacy Checkbox -->
      <div class="flex items-start">
        <div class="flex h-5 items-center">
          <p-checkbox
            id="agreedToPrivacy"
            formControlName="agreedToPrivacy"
            binary="true"
          ></p-checkbox>
        </div>
        <div class="ml-3 text-sm">
          <label
            for="agreedToPrivacy"
            class="cursor-pointer text-greyscale-700"
          >
            {{ "register.agreeToPrivacy" | transloco }}
            <a
              [href]="privacyUrl"
              target="_blank"
              class="text-primary-600 underline hover:text-primary-700"
              (click)="$event.stopPropagation()"
            >
              {{ "register.privacyTooltip" | transloco }}
            </a>
          </label>
        </div>
      </div>

      @if (
        accountForm.get("agreedToPrivacy")?.errors &&
        accountForm.get("agreedToPrivacy")?.touched
      ) {
        <div class="mt-1">
          <small class="text-error-600">
            {{ "register.mustAgreeToPrivacy" | transloco }}
          </small>
        </div>
      }

      <!-- Next Step Button -->
      <button
        type="button"
        (click)="onAccountStepComplete()"
        [disabled]="
          !accountForm.valid || !isEmailValid() || isEmailAlreadyTaken()
        "
        class="flex w-full justify-center rounded-md border border-transparent bg-primary-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {{ "register.nextStep" | transloco }}
      </button>
    </form>

    <!-- Login Link -->
    <div class="flex flex-col items-center gap-2 md:items-start">
      <a
        class="text-sm font-medium text-primary-600 no-underline hover:text-primary-500"
        routerLink="/auth/login"
      >
        {{ "register.alreadyHaveAccountQuestion" | transloco }}
        {{ "register.logIn" | transloco }}
      </a>
    </div>
  }

  <!-- Step 2: Create Company -->
  @if (currentView === ViewState.CREATE) {
    <app-register-create
      [accountData]="accountData!"
      (companyConflict)="onCompanyConflict($event)"
      (registrationComplete)="onRegistrationComplete()"
    >
    </app-register-create>
  }

  <!-- Step 2: Join Company -->
  @if (currentView === ViewState.JOIN) {
    <app-register-join
      [accountData]="accountData!"
      [targetCompany]="targetCompany!"
      (registrationComplete)="onRegistrationComplete()"
    >
    </app-register-join>
  }

  <!-- Toast Messages -->
  <p-toast></p-toast>
</div>
